import type { StoredBubblePosition } from "./bubble-position";

/**
 * Fired on `window` when a stored position is cleared, so the mounted bubble
 * can animate home. A DOM event rather than shared React state because the
 * position lives in motion values and must never cause a render.
 *
 * Module-private, and it carries the `storageKey` it applies to: two bubbles
 * exist (Birdie on the staff surfaces, support chat on the member surface),
 * and an undiscriminated event would teleport both. Callers subscribe through
 * `onBubblePositionReset`, which does the filtering and the `CustomEvent` cast
 * so no caller has to know either.
 */
const RESET_EVENT = "bubble-position-reset";

type ResetEventDetail = { storageKey: string };

/**
 * Where the user parked a bubble, persisted across reloads.
 *
 * Storage access is guarded for the same reason as `useStoredConversationId`:
 * sandboxed embeds throw on `localStorage`, and losing the position must not
 * crash the surface. The read is deliberately untyped-in / validated-out —
 * older builds and devtools both write into these keys, and
 * `resolveBubblePosition` treats anything it doesn't recognise as "unset".
 */
export function readStoredBubblePosition(
  storageKey: string,
): StoredBubblePosition | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as StoredBubblePosition;
  } catch {
    return null;
  }
}

export function writeStoredBubblePosition(
  storageKey: string,
  value: StoredBubblePosition,
): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Sandboxed context — the in-session position still works.
  }
}

export function resetStoredBubblePosition(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Ignore — the event below still moves the live bubble home.
  }
  window.dispatchEvent(
    new CustomEvent<ResetEventDetail>(RESET_EVENT, { detail: { storageKey } }),
  );
}

/** Subscribe to resets of one key. Returns the unsubscriber. */
export function onBubblePositionReset(
  storageKey: string,
  handler: () => void,
): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<ResetEventDetail>).detail;
    if (detail?.storageKey !== storageKey) return;
    handler();
  };

  window.addEventListener(RESET_EVENT, listener);
  return () => window.removeEventListener(RESET_EVENT, listener);
}
